import React, { useState, useEffect, useMemo } from 'react';
import { useWBL } from '../../context/WBLContext';
import { db } from '../../firebase';
import { 
    Plus, 
    Database, 
    School, 
    Map, 
    Edit2, 
    Trash2, 
    ChevronDown, 
    ChevronRight, 
    GraduationCap, 
    BookOpen, 
    MapPin, 
    Building,
    FolderPlus,
    Search,
    Globe,
    FolderTree,
    Maximize2,
    Minimize2
} from 'lucide-react';
import { collection, addDoc, onSnapshot, updateDoc, doc, deleteDoc } from 'firebase/firestore';

export const MetadataTabContent: React.FC = () => {
    const { tenants } = useWBL();

    const [districts, setDistricts] = useState<any[]>([]);
    const [schools, setSchools] = useState<any[]>([]);
    const [regions, setRegions] = useState<any[]>([]);
    
    // Search & Filter States
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRegionFilter, setSelectedRegionFilter] = useState('all');

    // Closed by default for all regions and districts
    const [expandedRegions, setExpandedRegions] = useState<Record<string, boolean>>({});
    const [expandedDistricts, setExpandedDistricts] = useState<Record<string, boolean>>({});

    // Modal & Form States
    const [isAddDistrictModalOpen, setIsAddDistrictModalOpen] = useState(false);
    const [isAddSchoolModalOpen, setIsAddSchoolModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<{ type: 'district' | 'school', id: string, name: string, level?: string, category?: string } | null>(null);
    const [deletingItem, setDeletingItem] = useState<{ type: 'district' | 'school', id: string, name: string } | null>(null);
    const [statusMessage, setStatusMessage] = useState<{ text: string, isError: boolean } | null>(null);

    const [newDistrict, setNewDistrict] = useState({ name: '', regionId: '' });
    const [newSchool, setNewSchool] = useState({ name: '', districtId: '', level: 'High School', category: 'Traditional' });

    useEffect(() => {
        const unsubscribeDistricts = onSnapshot(collection(db, 'districts'), (snapshot) => {
            setDistricts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (error) => console.warn("Districts snap error:", error.message));
        
        const unsubscribeSchools = onSnapshot(collection(db, 'schools'), (snapshot) => {
            setSchools(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (error) => console.warn("Schools snap error:", error.message));
        
        const unsubscribeRegions = onSnapshot(collection(db, 'regions'), (snapshot) => {
            const regionsList: any[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRegions(regionsList);
        }, (error) => console.warn("Regions snap error:", error.message));
        
        return () => { unsubscribeDistricts(); unsubscribeSchools(); unsubscribeRegions(); };
    }, []);

    const toggleRegion = (regionId: string) => {
        setExpandedRegions(prev => ({ ...prev, [regionId]: !prev[regionId] }));
    };

    const toggleDistrict = (districtId: string) => {
        setExpandedDistricts(prev => ({ ...prev, [districtId]: !prev[districtId] }));
    };

    const expandAll = () => {
        const regMap: Record<string, boolean> = {};
        regions.forEach(r => { regMap[r.id] = true; });
        const distMap: Record<string, boolean> = {};
        districts.forEach(d => { distMap[d.id] = true; });
        setExpandedRegions(regMap);
        setExpandedDistricts(distMap);
    };

    const collapseAll = () => {
        setExpandedRegions({});
        setExpandedDistricts({});
    };

    const openAddDistrictForRegion = (regionId: string) => {
        setNewDistrict({ name: '', regionId: regionId });
        setIsAddDistrictModalOpen(true);
    };

    const openAddSchoolForDistrict = (districtId: string, prefilledLevel: string = 'High School') => {
        setNewSchool({ name: '', districtId: districtId, level: prefilledLevel, category: 'Traditional' });
        setIsAddSchoolModalOpen(true);
    };

    const addDistrict = async () => {
        if (!newDistrict.name || !newDistrict.regionId) return;
        await addDoc(collection(db, 'districts'), newDistrict);
        setStatusMessage({ text: `Added District "${newDistrict.name}" successfully!`, isError: false });
        setNewDistrict({ name: '', regionId: '' });
        setIsAddDistrictModalOpen(false);
        setTimeout(() => setStatusMessage(null), 4000);
    };

    const addSchool = async () => {
        if (!newSchool.name || !newSchool.districtId || !newSchool.level) return;
        await addDoc(collection(db, 'schools'), {
            name: newSchool.name,
            districtId: newSchool.districtId,
            level: newSchool.level,
            category: newSchool.category || 'Traditional'
        });
        setStatusMessage({ text: `Added ${newSchool.level} "${newSchool.name}" successfully!`, isError: false });
        setNewSchool({ name: '', districtId: '', level: 'High School', category: 'Traditional' });
        setIsAddSchoolModalOpen(false);
        setTimeout(() => setStatusMessage(null), 4000);
    };

    const deleteItem = (type: 'district' | 'school', id: string, name: string) => {
        setDeletingItem({ type, id, name });
    };

    const confirmDelete = async () => {
        if (!deletingItem) return;
        const { type, id, name } = deletingItem;
        try {
            const docRef = doc(db, type === 'district' ? 'districts' : 'schools', id);
            await deleteDoc(docRef);
            setStatusMessage({ text: `Successfully deleted ${type === 'district' ? 'District' : 'School'} "${name}"`, isError: false });
            setTimeout(() => setStatusMessage(null), 4000);
        } catch (error) {
            console.error("Delete failed:", error);
            setStatusMessage({ text: "Delete failed: " + (error instanceof Error ? error.message : String(error)), isError: true });
            setTimeout(() => setStatusMessage(null), 6000);
        } finally {
            setDeletingItem(null);
        }
    };

    const updateItem = async () => {
        if (!editingItem) return;
        const ref = doc(db, editingItem.type === 'district' ? 'districts' : 'schools', editingItem.id);
        const data: any = { name: editingItem.name };
        if (editingItem.type === 'school') {
            data.level = editingItem.level;
            data.category = editingItem.category || 'Traditional';
        }
        await updateDoc(ref, data);
        setStatusMessage({ text: `Updated ${editingItem.type === 'district' ? 'District' : 'School'} "${editingItem.name}" successfully!`, isError: false });
        setEditingItem(null);
        setTimeout(() => setStatusMessage(null), 4000);
    };

    // Calculate metadata statistics
    const stats = useMemo(() => {
        return {
            regionsCount: regions.length,
            districtsCount: districts.length,
            highSchoolsCount: schools.filter(s => s.level === 'High School').length,
            postSecondaryCount: schools.filter(s => s.level === 'Post-Secondary').length,
            totalItems: regions.length + districts.length + schools.length
        };
    }, [regions, districts, schools]);

    // Sorted regions with applied filters
    const filteredRegions = useMemo(() => {
        const list = [...regions].sort((a, b) => (a.name || a.id || '').localeCompare(b.name || b.id || ''));
        return list.filter(region => {
            if (selectedRegionFilter !== 'all' && region.id !== selectedRegionFilter) return false;
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase().trim();
                const regDistricts = districts.filter(d => d.regionId === region.id);
                const regDistrictIds = regDistricts.map(d => d.id);
                const regSchools = schools.filter(s => regDistrictIds.includes(s.districtId));

                const matchesRegionName = (region.name || region.id || '').toLowerCase().includes(q);
                const matchesDistrictName = regDistricts.some(d => (d.name || '').toLowerCase().includes(q));
                const matchesSchoolName = regSchools.some(s => (s.name || '').toLowerCase().includes(q));

                if (!matchesRegionName && !matchesDistrictName && !matchesSchoolName) return false;
            }
            return true;
        });
    }, [regions, districts, schools, selectedRegionFilter, searchQuery]);

    const isSearching = !!searchQuery.trim();

    return (
        <div className="w-full bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-4 sm:p-5 space-y-4 font-sans">
            
            {/* Header Operations Toolbar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2.5 shrink-0">
                    <Database className="w-5 h-5 text-[#0094c8]" />
                    <h2 className="text-sm sm:text-base font-black text-[#011d3c] tracking-tight uppercase">Registry Hierarchy</h2>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-slate-100 text-slate-600">
                        {stats.regionsCount} Regions • {stats.districtsCount} Districts • {stats.highSchoolsCount + stats.postSecondaryCount} Schools
                    </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    {/* Region Filter */}
                    <div className="relative shrink-0">
                        <Globe className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                        <select
                            value={selectedRegionFilter}
                            onChange={(e) => setSelectedRegionFilter(e.target.value)}
                            className="appearance-none pl-8 pr-7 py-1.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 bg-white focus:ring-1 focus:ring-[#0094c8] outline-none uppercase cursor-pointer"
                        >
                            <option value="all">All Regions</option>
                            {[...regions, ...tenants]
                                .filter((r, i, arr) => arr.findIndex(t => t.id === r.id) === i)
                                .map(r => (
                                    <option key={r.id} value={r.id}>{(r.name || r.id).toUpperCase()}</option>
                                ))}
                        </select>
                        <ChevronDown className="w-3 h-3 text-slate-400 absolute right-2 top-2.5 pointer-events-none" />
                    </div>

                    {/* Search Bar */}
                    <div className="relative flex-1 min-w-[200px] w-full">
                        <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Search regions, districts, schools..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-[#0094c8] outline-none placeholder-slate-400 bg-white w-full font-medium"
                        />
                    </div>

                    {/* Expand/Collapse All */}
                    <button
                        type="button"
                        onClick={() => {
                            const anyExpanded = Object.values(expandedRegions).some(Boolean);
                            if (anyExpanded) collapseAll();
                            else expandAll();
                        }}
                        className="px-2.5 py-1.5 text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all flex items-center gap-1 shrink-0 cursor-pointer"
                        title="Toggle Expand / Collapse All Tiers"
                    >
                        {Object.values(expandedRegions).some(Boolean) ? (
                            <>
                                <Minimize2 className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Close All</span>
                            </>
                        ) : (
                            <>
                                <Maximize2 className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Expand All</span>
                            </>
                        )}
                    </button>

                    {/* Action Buttons */}
                    <button 
                        type="button"
                        onClick={() => {
                            setNewDistrict({ name: '', regionId: regions[0]?.id || 'spartanburg' });
                            setIsAddDistrictModalOpen(true);
                        }} 
                        className="flex items-center space-x-1 px-2.5 py-1.5 text-xs font-black uppercase text-[#0094c8] bg-[#0094c8]/10 hover:bg-[#0094c8]/20 rounded-lg transition-all border border-[#0094c8]/20 shrink-0 cursor-pointer"
                    >
                        <Plus className="w-3.5 h-3.5" /> 
                        <span>Add District</span>
                    </button>
                    <button 
                        type="button"
                        onClick={() => {
                            setNewSchool({ name: '', districtId: districts[0]?.id || '', level: 'High School', category: 'Traditional' });
                            setIsAddSchoolModalOpen(true);
                        }} 
                        className="flex items-center space-x-1 px-2.5 py-1.5 text-xs font-black uppercase text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-all border border-emerald-200 shrink-0 cursor-pointer"
                    >
                        <Plus className="w-3.5 h-3.5" /> 
                        <span>Add School</span>
                    </button>
                </div>
            </div>

            {statusMessage && (
                <div className={`p-3 rounded-xl text-xs font-black uppercase tracking-wider ${statusMessage.isError ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'} transition-all flex items-center justify-between shadow-2xs`}>
                    <span>{statusMessage.text}</span>
                    <button onClick={() => setStatusMessage(null)} className="text-[10px] underline cursor-pointer font-bold">Dismiss</button>
                </div>
            )}

            {/* SUCCINCT, STACKED, TIERS - CLOSED - TABLE STYLE */}
            <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs bg-white">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50/90 border-b border-slate-200">
                            <tr className="text-[10px] font-black uppercase tracking-wider text-slate-500 select-none">
                                <th className="py-3 px-4">Hierarchy Entity / Name</th>
                                <th className="py-3 px-4">Tier & Level</th>
                                <th className="py-3 px-4">Supervised Scope / Category</th>
                                <th className="py-3 px-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 text-xs">
                            {filteredRegions.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="py-12 text-center text-xs text-slate-400 font-bold uppercase tracking-wider bg-slate-50/30">
                                        No matching regions or entities found.
                                    </td>
                                </tr>
                            ) : (
                                filteredRegions.map(region => {
                                    const regDistricts = districts.filter(d => d.regionId === region.id)
                                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                                    const regDistrictIds = regDistricts.map(d => d.id);
                                    const regSchools = schools.filter(s => regDistrictIds.includes(s.districtId));
                                    const regHighSchools = regSchools.filter(s => s.level === 'High School');
                                    const regPostSec = regSchools.filter(s => s.level === 'Post-Secondary');

                                    // CLOSED BY DEFAULT unless user clicks expand OR is actively searching
                                    const isRegExpanded = isSearching ? true : (expandedRegions[region.id] === true);

                                    return (
                                        <React.Fragment key={region.id}>
                                            {/* TIER 1 ROW: REGION (Light Theme Tier Row) */}
                                            <tr 
                                                onClick={() => toggleRegion(region.id)}
                                                className="bg-slate-100/90 hover:bg-slate-200/70 text-slate-900 transition-colors border-b border-slate-200 cursor-pointer select-none"
                                            >
                                                <td className="py-3 px-4 font-black">
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-slate-500">
                                                            {isRegExpanded ? <ChevronDown className="w-4 h-4 text-[#0094c8]" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                                                        </span>
                                                        <MapPin className="w-4 h-4 text-[#0094c8] shrink-0" />
                                                        <span className="text-sm font-extrabold text-[#011d3c] tracking-tight">{region.name || region.id}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className="px-2 py-0.5 rounded text-[9.5px] font-black uppercase tracking-wider bg-[#0094c8]/15 text-[#0094c8] border border-[#0094c8]/30">
                                                        Tier 1 • Region
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-[11px] font-bold text-slate-600">
                                                    {regDistricts.length} Districts • {regHighSchools.length} High Schools • {regPostSec.length} Post-Secondaries
                                                </td>
                                                <td className="py-3 px-4 text-right" onClick={e => e.stopPropagation()}>
                                                    <div className="flex items-center justify-end space-x-1.5">
                                                        <button
                                                            type="button"
                                                            onClick={() => openAddDistrictForRegion(region.id)}
                                                            className="px-2.5 py-1 text-[10px] font-black uppercase text-[#0094c8] bg-white hover:bg-slate-50 rounded-md border border-[#0094c8]/30 shadow-2xs transition-all cursor-pointer"
                                                        >
                                                            + Add District
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* TIER 2 & TIER 3 SUB-ROWS UNDER THIS REGION */}
                                            {isRegExpanded && (
                                                regDistricts.length === 0 ? (
                                                    <tr className="bg-slate-50/50">
                                                        <td colSpan={4} className="py-3 pl-12 text-slate-400 text-[11px] italic font-medium">
                                                            No districts created in {region.name || region.id} yet.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    regDistricts.map(district => {
                                                        const distSchools = schools.filter(s => s.districtId === district.id);
                                                        const distHighSchools = distSchools.filter(s => s.level === 'High School').sort((a,b) => (a.name || '').localeCompare(b.name || ''));
                                                        const distPostSec = distSchools.filter(s => s.level === 'Post-Secondary').sort((a,b) => (a.name || '').localeCompare(b.name || ''));

                                                        // CLOSED BY DEFAULT unless expanded
                                                        const isDistExpanded = isSearching ? true : (expandedDistricts[district.id] === true);

                                                        return (
                                                            <React.Fragment key={district.id}>
                                                                {/* TIER 2 ROW: DISTRICT */}
                                                                <tr 
                                                                    onClick={() => toggleDistrict(district.id)}
                                                                    className="bg-slate-50 hover:bg-slate-100 transition-colors border-b border-slate-200 cursor-pointer select-none"
                                                                >
                                                                    <td className="py-2.5 px-4 pl-10 font-bold text-slate-900">
                                                                        <div className="flex items-center space-x-2">
                                                                            <span className="text-slate-400">
                                                                                {isDistExpanded ? <ChevronDown className="w-3.5 h-3.5 text-sky-600" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                                                                            </span>
                                                                            <Map className="w-3.5 h-3.5 text-sky-600 shrink-0" />
                                                                            <span>{district.name}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="py-2.5 px-4">
                                                                        <span className="px-2 py-0.5 rounded text-[9.5px] font-black uppercase tracking-wider bg-sky-50 text-sky-800 border border-sky-200">
                                                                            Tier 2 • District
                                                                        </span>
                                                                    </td>
                                                                    <td className="py-2.5 px-4 text-xs font-semibold text-slate-600">
                                                                        {distHighSchools.length} High Schools • {distPostSec.length} Post-Secondaries
                                                                    </td>
                                                                    <td className="py-2.5 px-4 text-right" onClick={e => e.stopPropagation()}>
                                                                        <div className="flex items-center justify-end space-x-1">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => openAddSchoolForDistrict(district.id, 'High School')}
                                                                                className="px-2 py-1 text-[9.5px] font-black uppercase text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded border border-emerald-200 transition-all cursor-pointer"
                                                                                title="Add High School"
                                                                            >
                                                                                + Add HS
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => openAddSchoolForDistrict(district.id, 'Post-Secondary')}
                                                                                className="px-2 py-1 text-[9.5px] font-black uppercase text-amber-800 bg-amber-50 hover:bg-amber-100 rounded border border-amber-200 transition-all cursor-pointer"
                                                                                title="Add Post-Secondary"
                                                                            >
                                                                                + Add PS
                                                                            </button>
                                                                            <button 
                                                                                type="button"
                                                                                onClick={() => setEditingItem({ type: 'district', id: district.id, name: district.name })}
                                                                                className="p-1 text-slate-400 hover:text-[#0094c8] hover:bg-slate-200/60 rounded transition-colors cursor-pointer"
                                                                                title="Edit District"
                                                                            >
                                                                                <Edit2 className="w-3.5 h-3.5" />
                                                                            </button>
                                                                            <button 
                                                                                type="button"
                                                                                onClick={() => deleteItem('district', district.id, district.name)}
                                                                                className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                                                                title="Delete District"
                                                                            >
                                                                                <Trash2 className="w-3.5 h-3.5" />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>

                                                                {/* TIER 3 SUB-ROWS: SCHOOLS UNDER DISTRICT */}
                                                                {isDistExpanded && (
                                                                    distSchools.length === 0 ? (
                                                                        <tr className="bg-white">
                                                                            <td colSpan={4} className="py-2.5 pl-16 text-slate-400 text-[11px] italic font-medium border-b border-slate-100">
                                                                                No schools registered in this district yet.
                                                                            </td>
                                                                        </tr>
                                                                    ) : (
                                                                        distSchools
                                                                            .sort((a,b) => (a.level || '').localeCompare(b.level || '') || (a.name || '').localeCompare(b.name || ''))
                                                                            .map(school => (
                                                                                <tr key={school.id} className="bg-white hover:bg-slate-50 transition-colors border-b border-slate-100 group">
                                                                                    {/* Name with deep indent */}
                                                                                    <td className="py-2 px-4 pl-16 font-bold text-slate-900">
                                                                                        <div className="flex items-center space-x-2">
                                                                                            {school.level === 'Post-Secondary' ? (
                                                                                                <BookOpen className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                                                                            ) : (
                                                                                                <GraduationCap className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                                                                            )}
                                                                                            <span className="text-xs text-slate-800">{school.name}</span>
                                                                                        </div>
                                                                                    </td>

                                                                                    {/* Level */}
                                                                                    <td className="py-2 px-4">
                                                                                        <span className={`px-2 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider ${
                                                                                            school.level === 'Post-Secondary' 
                                                                                                ? 'bg-amber-50 text-amber-800 border border-amber-200' 
                                                                                                : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                                                                        }`}>
                                                                                            {school.level === 'Post-Secondary' ? 'Tier 4 • Post-Secondary' : 'Tier 3 • High School'}
                                                                                        </span>
                                                                                    </td>

                                                                                    {/* Category */}
                                                                                    <td className="py-2 px-4">
                                                                                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                                                                                            school.category === 'Charter' ? 'bg-purple-50 text-purple-700 border border-purple-200' :
                                                                                            school.category === 'Career & Tech' ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                                                                                            'bg-slate-100 text-slate-600 border border-slate-200'
                                                                                        }`}>
                                                                                            {school.category || 'Traditional'}
                                                                                        </span>
                                                                                    </td>

                                                                                    {/* Actions */}
                                                                                    <td className="py-2 px-4 text-right">
                                                                                        <div className="flex items-center justify-end space-x-1 opacity-80 group-hover:opacity-100 transition-opacity">
                                                                                            <button 
                                                                                                type="button"
                                                                                                onClick={() => setEditingItem({ type: 'school', id: school.id, name: school.name, level: school.level || 'High School', category: school.category || 'Traditional' })}
                                                                                                className="p-1 text-slate-400 hover:text-[#0094c8] hover:bg-slate-100 rounded transition-colors cursor-pointer"
                                                                                                title="Edit School"
                                                                                            >
                                                                                                <Edit2 className="w-3.5 h-3.5" />
                                                                                            </button>
                                                                                            <button 
                                                                                                type="button"
                                                                                                onClick={() => deleteItem('school', school.id, school.name)}
                                                                                                className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                                                                                title="Delete School"
                                                                                            >
                                                                                                <Trash2 className="w-3.5 h-3.5" />
                                                                                            </button>
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                            ))
                                                                    )
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })
                                                )
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Editing Modal */}
            {editingItem && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 animate-fadeIn">
                    <div className="bg-white p-6 sm:p-8 rounded-3xl w-full max-w-md shadow-2xl border border-slate-100 space-y-5">
                        <div className="space-y-1">
                            <h3 className="text-lg font-black text-[#011d3c] uppercase tracking-tight">Edit {editingItem.type}</h3>
                            <p className="text-xs text-slate-500">Modify properties of this registry record.</p>
                        </div>
                        <div className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Name</label>
                                <input 
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#0094c8] transition-all text-xs font-bold text-slate-800" 
                                    value={editingItem.name} 
                                    onChange={e => setEditingItem({...editingItem, name: e.target.value})} 
                                />
                            </div>
                            {editingItem.type === 'school' && (
                                 <>
                                     <div className="space-y-1">
                                         <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Educational Level</label>
                                         <select 
                                             className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#0094c8] transition-all text-xs font-bold text-slate-800" 
                                             value={editingItem.level} 
                                             onChange={e => setEditingItem({...editingItem, level: e.target.value})}
                                         >
                                             <option value="High School">High School</option>
                                             <option value="Post-Secondary">Post-Secondary</option>
                                         </select>
                                     </div>
                                     <div className="space-y-1">
                                         <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Category</label>
                                         <select 
                                             className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#0094c8] transition-all text-xs font-bold text-slate-800" 
                                             value={editingItem.category || 'Traditional'} 
                                             onChange={e => setEditingItem({...editingItem, category: e.target.value})}
                                         >
                                             <option value="Traditional">Traditional</option>
                                             <option value="Charter">Charter</option>
                                             <option value="Career & Tech">Career & Tech</option>
                                         </select>
                                     </div>
                                 </>
                            )}
                        </div>
                        <div className="pt-2 flex justify-end space-x-3">
                            <button onClick={() => setEditingItem(null)} className="px-4 py-2 text-xs font-black uppercase text-slate-500 hover:text-slate-800 transition-colors cursor-pointer">Cancel</button>
                            <button onClick={updateItem} className="px-4 py-2 bg-[#0094c8] text-white text-xs font-black uppercase tracking-wider rounded-xl hover:bg-[#0094c8]/90 shadow-sm transition-all cursor-pointer">Save Changes</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add District Modal */}
            {isAddDistrictModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 animate-fadeIn">
                    <div className="bg-white p-6 sm:p-8 rounded-3xl w-full max-w-md shadow-2xl border border-slate-100 space-y-5">
                        <div className="space-y-1">
                            <h3 className="text-lg font-black text-[#011d3c] uppercase tracking-tight">Add New District</h3>
                            <p className="text-xs text-slate-500">Create a new school district organizational block.</p>
                        </div>
                        <div className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">District Name</label>
                                <input 
                                    placeholder="e.g. Spartanburg School District 7" 
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#0094c8] transition-all text-xs font-bold text-slate-800" 
                                    value={newDistrict.name} 
                                    onChange={e => setNewDistrict({...newDistrict, name: e.target.value})} 
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Parent Region</label>
                                <select 
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#0094c8] transition-all text-xs font-bold text-slate-800" 
                                    value={newDistrict.regionId} 
                                    onChange={e => setNewDistrict({...newDistrict, regionId: e.target.value})}
                                >
                                    <option value="">Select Region</option>
                                    {[...regions].sort((a,b) => (a.name || '').localeCompare(b.name || '')).map(r => (
                                        <option key={r.id} value={r.id}>{r.name || r.id}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="pt-2 flex justify-end space-x-3">
                             <button onClick={() => setIsAddDistrictModalOpen(false)} className="px-4 py-2 text-xs font-black uppercase text-slate-500 hover:text-slate-800 transition-colors cursor-pointer">Cancel</button>
                             <button onClick={addDistrict} className="px-4 py-2 bg-[#0094c8] text-white text-xs font-black uppercase tracking-wider rounded-xl hover:bg-[#0094c8]/90 shadow-sm transition-all cursor-pointer">Save District</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add School Modal */}
            {isAddSchoolModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 animate-fadeIn">
                    <div className="bg-white p-6 sm:p-8 rounded-3xl w-full max-w-md shadow-2xl border border-slate-100 space-y-5">
                        <div className="space-y-1">
                            <h3 className="text-lg font-black text-[#011d3c] uppercase tracking-tight">Add New School</h3>
                            <p className="text-xs text-slate-500">Register a new high school or post-secondary institution.</p>
                        </div>
                        <div className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">School / Institution Name</label>
                                <input 
                                    placeholder="e.g. Wando High School" 
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#0094c8] transition-all text-xs font-bold text-slate-800" 
                                    value={newSchool.name} 
                                    onChange={e => setNewSchool({...newSchool, name: e.target.value})} 
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Associated District</label>
                                <select 
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#0094c8] transition-all text-xs font-bold text-slate-800" 
                                    value={newSchool.districtId} 
                                    onChange={e => setNewSchool({...newSchool, districtId: e.target.value})}
                                >
                                    <option value="">Select District</option>
                                    {[...districts].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(d => (
                                        <option key={d.id} value={d.id}>{d.name || d.id}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Educational Level</label>
                                <select 
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#0094c8] transition-all text-xs font-bold text-slate-800" 
                                    value={newSchool.level} 
                                    onChange={e => setNewSchool({...newSchool, level: e.target.value})}
                                >
                                    <option value="High School">High School</option>
                                    <option value="Post-Secondary">Post-Secondary</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Category</label>
                                <select 
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#0094c8] transition-all text-xs font-bold text-slate-800" 
                                    value={newSchool.category || 'Traditional'} 
                                    onChange={e => setNewSchool({...newSchool, category: e.target.value})}
                                >
                                    <option value="Traditional">Traditional</option>
                                    <option value="Charter">Charter</option>
                                    <option value="Career & Tech">Career & Tech</option>
                                </select>
                            </div>
                        </div>
                        <div className="pt-2 flex justify-end space-x-3">
                             <button onClick={() => setIsAddSchoolModalOpen(false)} className="px-4 py-2 text-xs font-black uppercase text-slate-500 hover:text-slate-800 transition-colors cursor-pointer">Cancel</button>
                             <button onClick={addSchool} className="px-4 py-2 bg-emerald-600 text-white text-xs font-black uppercase tracking-wider rounded-xl hover:bg-emerald-700 shadow-sm transition-all cursor-pointer">Save School</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Delete Modal */}
            {deletingItem && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 animate-fadeIn">
                    <div className="bg-white p-6 rounded-3xl w-full max-w-sm shadow-2xl border border-slate-100 space-y-4">
                        <h3 className="text-base font-black text-red-600 uppercase tracking-tight">Delete {deletingItem.type === 'district' ? 'District' : 'School'}?</h3>
                        <p className="text-xs text-slate-500 leading-relaxed font-medium">
                            Are you sure you want to permanently delete the {deletingItem.type} <strong className="text-slate-900 font-extrabold">"{deletingItem.name}"</strong>? 
                            This action cannot be undone.
                        </p>
                        <div className="pt-2 flex justify-end space-x-3">
                            <button onClick={() => setDeletingItem(null)} className="px-4 py-2 text-xs font-bold uppercase text-slate-500 hover:text-slate-800 transition-colors cursor-pointer">Cancel</button>
                            <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-xs transition-all cursor-pointer">Delete Document</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};
